import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { SITE } from "@/content/site";
import { buildSeo } from "@/lib/seo";
import { ShieldCheck, Lock, Database, Users, Cookie, Mail } from "lucide-react";

const PRIVACY_CONTACT = "admin@freelancertamal.com";
const LAST_UPDATED = "June 21, 2026";

export const Route = createFileRoute("/trust")({
  head: () =>
    buildSeo({
      title: "Trust & Security — Freelancer Tamal",
      description:
        "How Freelancer Tamal handles your data, the subprocessors we use, retention, and how to reach us about privacy or security questions.",
      path: "/trust",
      ogEyebrow: "Trust",
    }),
  component: TrustPage,
});

type Card = { icon: typeof ShieldCheck; title: string; body: string };

const CARDS: Card[] = [
  {
    icon: Database,
    title: "What we collect",
    body:
      "Only what you type into the contact form: name, email, and your optional company, budget range, and message. No tracking profiles, no purchased lists.",
  },
  {
    icon: Lock,
    title: "How it's stored",
    body:
      "Form submissions land in a Google Workspace mailbox at our domain and, where used, an email/CRM tool that helps us reply. Access is limited to people who need it.",
  },
  {
    icon: Cookie,
    title: "Cookies & analytics",
    body:
      "We use only the cookies needed to run the site and, where enabled, Google Analytics 4 for aggregate traffic. No advertising trackers, no third-party retargeting pixels.",
  },
  {
    icon: Users,
    title: "Subprocessors",
    body:
      "Google (Workspace email, Search Console, Analytics), an email/CRM provider used for replies, and a managed edge hosting platform that serves the site.",
  },
  {
    icon: ShieldCheck,
    title: "Retention & deletion",
    body:
      "We keep your details for as long as our working relationship lasts and delete them on request. Email us and we will remove your records.",
  },
  {
    icon: Mail,
    title: "Contact us",
    body:
      "Privacy questions, deletion requests, and security disclosures all go to admin@freelancertamal.com. We aim to reply within one business day.",
  },
];

function TrustPage() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Trust", path: "/trust" }]} />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${SITE.url}/trust#webpage`,
          url: `${SITE.url}/trust`,
          name: "Trust & Security — Freelancer Tamal",
          description:
            "Trust, privacy, and security overview for the Freelancer Tamal website.",
          inLanguage: "en",
          isPartOf: { "@id": `${SITE.url}/#website` },
          dateModified: "2026-06-21",
        }}
      />
      <Section className="pt-10 pb-24">
        <header className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Trust</p>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">
            Trust &amp; Security
          </h1>
          <p className="mt-4 text-muted-foreground">
            This page is maintained by {SITE.fullName} to answer common security and privacy
            questions about {SITE.name}. It describes how this website actually works today —
            not a certification or third-party audit. Last updated {LAST_UPDATED}.
          </p>
        </header>

        <div className="grid gap-4 md:grid-cols-2 mt-10 max-w-5xl">
          {CARDS.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl border border-border/60 bg-card/40 p-6 hover:bg-card/60 transition"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-4.5 w-4.5" aria-hidden />
                </span>
                <h2 className="text-lg font-semibold">{title}</h2>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        <section className="max-w-3xl mt-14">
          <h2 className="text-2xl font-semibold tracking-tight">Shared responsibility</h2>
          <p className="mt-3 text-muted-foreground text-[15px] leading-relaxed">
            Hosting, TLS, and edge protections are provided by the managed platform that runs
            this site. {SITE.fullName} is responsible for the application, the data we receive
            through it, and how we work with the subprocessors listed above. You are responsible
            for the accuracy of the information you choose to share with us.
          </p>
        </section>

        <section className="max-w-3xl mt-10">
          <h2 className="text-2xl font-semibold tracking-tight">Reporting a security issue</h2>
          <p className="mt-3 text-muted-foreground text-[15px] leading-relaxed">
            If you believe you have found a vulnerability on {SITE.url}, please email{" "}
            <a href={`mailto:${PRIVACY_CONTACT}`} className="underline">
              {PRIVACY_CONTACT}
            </a>{" "}
            with steps to reproduce. We will acknowledge your report, investigate, and keep you
            informed as we remediate.
          </p>
        </section>

        <div className="mt-12 flex flex-wrap gap-3">
          <Link
            to="/privacy"
            className="inline-flex items-center rounded-full border border-border/70 px-5 py-2.5 text-sm hover:bg-card/60 transition"
          >
            Read the full privacy policy
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm text-primary-foreground hover:opacity-90 transition"
          >
            Contact us
          </Link>
        </div>
      </Section>
    </>
  );
}
